import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '@/lib/api';
import { FLIGHT_TYPES } from '@/lib/flightTypes';
import { formatInstant } from '@/lib/dates';
import { distanceLabel, placeLabel, type RouteFacts } from '@/lib/summary';
import { PageHeader, Card, Skeleton, EmptyState, ErrorNote, TypeBadge } from '@/components/ui';

interface Flight extends RouteFacts {
  id: string;
  date: string;
  departure_airport: string;
  arrival_airport: string;
  /** Flight time as entered; `null` until he enters one. */
  flight_hours: number | null;
  flight_type: string;
  operator?: string | null;
  notes?: string | null;
  /** The manifest — who was aboard, and who held PIC. */
  souls?: Array<{ id: string; initials: string; role_aboard: string }>;
  /** Derived by the API from the manifest — BA Law's column F. */
  headcount?: { souls_on_board: number };
}

/** A flight is a moment, not a calendar day: local is the right reading. `lib/dates.ts`. */
const fmtDate = (d: string) => formatInstant(d) ?? '—';
/** A leg with no flight time is shown as missing, never as 0.0. */
const hrs = (h: number | null) => (h == null ? '—' : Number(h).toFixed(2));

export default function FlightLog() {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<string>('ALL');

  useEffect(() => {
    apiGet<{ flights: Flight[] }>('/api/flights')
      .then((d) => setFlights(d.flights || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const shown = useMemo(
    () => (filter === 'ALL' ? flights : flights.filter((f) => f.flight_type === filter)),
    [flights, filter]
  );
  // Flight time and distance are summed over the legs that have them; the rest
  // are counted beside the sum, never read as zero.
  const totals = useMemo(() => ({
    hours: shown.reduce((s, f) => s + (f.flight_hours ?? 0), 0),
    missing: shown.filter((f) => f.flight_hours == null).length,
    nm: shown.reduce((s, f) => s + (f.distance_nm ?? 0), 0),
  }), [shown]);

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Flight Log"
        subtitle={
          `${shown.length} flight${shown.length === 1 ? '' : 's'} · ${totals.hours.toFixed(1)} h flight time · ` +
          `${totals.nm.toLocaleString()} nm` +
          (totals.missing > 0 ? ` · ${totals.missing} without a flight time` : '')
        }
        actions={
          <>
            {/* BA Law's format, landscape. Same document the backup email sends. */}
            <a href="/api/export/pdf" className="btn-ghost" title="The BA Law flight log, landscape">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
              </svg>
              Export PDF
            </a>
            <Link to="/flights/new" className="btn-primary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-5 w-5">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Log Flight
            </Link>
          </>
        }
      />

      {/* Type filter */}
      <div className="mb-4 flex flex-wrap gap-2">
        {['ALL', ...FLIGHT_TYPES.map((t) => t.code)].map((code) => (
          <button
            key={code}
            onClick={() => setFilter(code)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              filter === code
                ? 'bg-avio/20 text-white ring-1 ring-avio/40'
                : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-slate-200'
            }`}
          >
            {code === 'ALL' ? 'All' : code}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : error ? (
        <ErrorNote message={error} />
      ) : shown.length === 0 ? (
        <Card>
          <EmptyState
            title={filter === 'ALL' ? 'No flights logged yet' : 'No flights of this type'}
            hint={filter === 'ALL' ? 'Log your first flight to start your N313G logbook.' : undefined}
            action={filter === 'ALL' ? <Link to="/flights/new" className="btn-primary mt-1">Log a flight</Link> : undefined}
          />
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="hidden overflow-hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-3 font-medium">Date</th>
                    <th className="px-5 py-3 font-medium">Route</th>
                    <th className="px-5 py-3 font-medium">Type</th>
                    <th className="px-5 py-3 font-medium">PIC</th>
                    <th className="px-5 py-3 text-right font-medium" title="Flight time, as entered">Time</th>
                    <th className="px-5 py-3 text-right font-medium" title="Great-circle distance between the two airports">Distance</th>
                    <th className="px-5 py-3 text-right font-medium" title="Souls on board — BA Law's headcount">Souls</th>
                    <th className="px-5 py-3 font-medium">Operator</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((f) => (
                    <tr key={f.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]">
                      <td className="px-5 py-3 text-slate-300"><Link to={`/flights/${f.id}`} className="block">{fmtDate(f.date)}</Link></td>
                      <td className="px-5 py-3">
                        <Link to={`/flights/${f.id}`} className="ident text-white">{f.departure_airport} → {f.arrival_airport}</Link>
                        {placeLabel(f.destination) && <p className="text-xs text-slate-500">{placeLabel(f.destination)}</p>}
                      </td>
                      <td className="px-5 py-3"><TypeBadge code={f.flight_type} /></td>
                      <td className="px-5 py-3 text-slate-400">{f.souls?.find((p) => p.role_aboard === 'PIC')?.initials || '—'}</td>
                      <td className={`px-5 py-3 text-right tabular-nums ${f.flight_hours == null ? 'text-amber-300' : 'text-slate-300'}`} title={f.flight_hours == null ? 'No flight time entered yet' : undefined}>{hrs(f.flight_hours)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-400">{distanceLabel(f)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-slate-400">{f.headcount?.souls_on_board ?? 0}</td>
                      <td className="px-5 py-3 text-slate-400">{f.operator || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {shown.map((f) => (
              <Link key={f.id} to={`/flights/${f.id}`} className="block">
                <Card hover className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="ident text-white">{f.departure_airport} → {f.arrival_airport}</span>
                    <TypeBadge code={f.flight_type} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                    <span>{fmtDate(f.date)}</span>
                    <span className="tabular-nums">{f.flight_hours == null ? 'no flight time' : `${hrs(f.flight_hours)} h`} · {distanceLabel(f)} · {f.headcount?.souls_on_board ?? 0} souls · PIC {f.souls?.find((p) => p.role_aboard === 'PIC')?.initials || '—'}</span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
